import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2 } from 'lucide-react';
import { cn, FilterCombobox } from '@aaas/shared';
import { useRejectionReasonListQuery } from '../hooks/useRejectionReasonQueries';

interface RejectReasonModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reasonId: string, customText: string) => Promise<void>;
  title?: string;
  description?: string;
  workspaceId: string | undefined;
  idToken: string | undefined;
  className?: string;
}

export function RejectReasonModal({
  open,
  onClose,
  onConfirm,
  title,
  description,
  workspaceId,
  idToken,
  className,
}: RejectReasonModalProps) {
  const { t } = useTranslation('ats');
  const [selectedReasonId, setSelectedReasonId] = useState<string | null>(null);
  const [customReasonText, setCustomReasonText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Fetch rejection reasons (only when modal is open)
  const { data: rejectionReasonsData } = useRejectionReasonListQuery({
    workspaceId,
    idToken,
    enabled: open,
  });

  const handleClose = () => {
    setSelectedReasonId(null);
    setCustomReasonText('');
    onClose();
  };

  const handleConfirm = async () => {
    if (!selectedReasonId) return;

    setIsProcessing(true);
    try {
      await onConfirm(selectedReasonId, customReasonText);
      // Reset state after successful confirmation
      setSelectedReasonId(null);
      setCustomReasonText('');
    } catch (err) {
      // Error is handled by parent component
      console.error('Error in RejectReasonModal:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!open) return null;

  return (
    <div className={cn("fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4", className)} style={{ margin: 0 }}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden"
        style={{ margin: 0 }}
      >
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{title || t('rejectReason.defaultTitle')}</h3>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal body */}
        <div className="px-6 py-4">
          <p className="text-sm text-gray-600 mb-4">{description || t('rejectReason.defaultDescription')}</p>

          {/* Rejection reason dropdown */}
          <FilterCombobox
            options={
              rejectionReasonsData?.data?.items.map((r) => ({
                value: r.id,
                label: r.reason,
              })) || []
            }
            value={selectedReasonId || undefined}
            onChange={(val) => setSelectedReasonId(val || null)}
            placeholder={t('rejectReason.selectPlaceholder')}
            searchPlaceholder={t('rejectReason.searchPlaceholder')}
            emptyMessage={t('rejectReason.emptyMessage')}
            popoverWidth="w-[400px]"
            className="w-full"
            showClearOption={false}
          />

          {/* Optional custom text */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('rejectReason.additionalNotes')}
            </label>
            <textarea
              placeholder={t('rejectReason.notesPlaceholder')}
              value={customReasonText}
              onChange={(e) => setCustomReasonText(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>
        </div>

        {/* Modal footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            {t('rejectReason.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedReasonId || isProcessing}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isProcessing && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('rejectReason.confirmButton')}
          </button>
        </div>
      </div>
    </div>
  );
}
